/**
 * GlobalSearch — Command Palette accessibile da tutte le pagine.
 *
 * Apertura:
 *  - ⌘K / Ctrl+K (nel caso il focus non sia in un input)
 *  - Click sul pulsante "🔎 Cerca" nella navbar
 *
 * Chiama `GET /api/search?q=...` con debounce 250ms, mostra i risultati
 * raggruppati (cantieri, lavoratori, rapportini, bolle, magazzino, ferie, utenti)
 * con navigazione tastiera (↑ ↓ Enter Esc).
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import api from "@/api";

const GROUP_LABELS = {
  cantieri: "Cantieri",
  lavoratori: "Lavoratori",
  rapportini: "Rapportini",
  bolle: "Bolle",
  magazzino: "Richieste magazzino",
  ferie: "Ferie / Permessi",
  utenti: "Utenti & Ruoli",
};

const GROUP_ORDER = ["cantieri", "lavoratori", "rapportini", "bolle", "magazzino", "ferie", "utenti"];

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ groups: {}, total: 0 });
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Reset all'apertura
  useEffect(() => {
    if (open) {
      setQ("");
      setResults({ groups: {}, total: 0 });
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return undefined;
    if (q.trim().length < 2) {
      setResults({ groups: {}, total: 0 });
      return undefined;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/search`, { params: { q: q.trim(), limit: 6 } });
        setResults(r.data);
        setActiveIdx(0);
      } catch {
        setResults({ groups: {}, total: 0 });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  // Flatten per navigazione tastiera
  const flat = [];
  GROUP_ORDER.forEach((g) => {
    (results.groups?.[g] || []).forEach((item) => flat.push({ ...item, _group: g }));
  });

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && flat[activeIdx]) {
      e.preventDefault();
      openItem(flat[activeIdx]);
    } else if (e.key === "Escape") {
      onClose?.();
    }
  };

  const openItem = (item) => {
    if (!item?.url) return;
    onClose?.();
    navigate(item.url);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-24"
      onClick={onClose}
      data-testid="global-search-overlay"
    >
      <div
        className="w-full max-w-2xl bg-white border border-slate-200 rounded-md shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
          {loading ? (
            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-slate-400" />
          )}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Cerca cantieri, lavoratori, bolle, rapportini, ferie…"
            className="flex-1 outline-none text-sm bg-transparent"
            data-testid="global-search-input"
          />
          <kbd className="hidden sm:inline-block text-[10px] font-mono px-1.5 py-0.5 border border-slate-300 rounded-sm text-slate-500">
            ESC
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
          {q.trim().length < 2 ? (
            <div className="p-8 text-center text-xs text-slate-400">
              Digita almeno 2 caratteri per cercare…
            </div>
          ) : results.total === 0 && !loading ? (
            <div className="p-8 text-center text-xs text-slate-400">
              Nessun risultato per "<span className="font-semibold">{q}</span>".
            </div>
          ) : (
            <div className="py-2">
              {GROUP_ORDER.map((g) => {
                const items = results.groups?.[g];
                if (!items || items.length === 0) return null;
                return (
                  <div key={g} className="mb-1">
                    <div className="px-4 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {GROUP_LABELS[g]}
                    </div>
                    {items.map((item) => {
                      const idx = flat.findIndex(
                        (f) => f._group === g && f.id === item.id,
                      );
                      const active = idx === activeIdx;
                      return (
                        <button
                          key={`${g}-${item.id}`}
                          onClick={() => openItem(item)}
                          onMouseEnter={() => setActiveIdx(idx)}
                          className={`w-full text-left px-4 py-2 flex items-center gap-3 border-l-2 ${active ? "bg-slate-100 border-slate-800" : "border-transparent hover:bg-slate-50"}`}
                          data-testid={`global-search-item-${g}-${item.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-800 truncate">{item.title}</div>
                            {item.subtitle && (
                              <div className="text-[11px] text-slate-500 truncate">{item.subtitle}</div>
                            )}
                          </div>
                          {item.meta && (
                            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 px-1.5 py-0.5 border border-slate-200 rounded-sm">
                              {item.meta}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 flex items-center gap-3">
          <span><kbd className="font-mono">↑ ↓</kbd> naviga</span>
          <span><kbd className="font-mono">↵</kbd> apri</span>
          <span><kbd className="font-mono">Esc</kbd> chiudi</span>
          {results.total > 0 && <span className="ml-auto">{results.total} risultati</span>}
        </div>
      </div>
    </div>
  );
}
